import React, { useState, useEffect, useRef } from 'react';
import Modal from 'cccisd-modal';
import { Formik, Form, Field, CccisdToggle, CccisdFieldWrapper } from 'cccisd-formik';
import DatePicker from 'cccisd-datepicker';
import { useDispatch, useSelector } from 'react-redux';
import _get from 'lodash/get';
import axios from 'cccisd-axios';
import notification from 'cccisd-notification';
import Loader from 'cccisd-loader';

import RequiredFieldLabel from '../../components/Manage/RequiredFieldLabel';

import { queryAdministerPlansList, queryAdministerYouthPlans } from '../../reducers/administer/administerSlice';
import { queryCareCoordinatorYouthList } from '../../reducers/careCoordinator/careCoordinatorSlice';

import { isMonday, getFormattedDateForDB, getPlanStatus, switchPlanNameIfNeeded, noYouthWarning } from './helpers.js';
import YouthCard from './components/YouthCard.js';
import PlanCard from './components/PlanCard.js';
import YouthSelectionTable from './components/YouthSelectionTable.js';

import style from './style.css';

const Boilerplate = window.cccisd && window.cccisd.boilerplate;

const Administer = () => {
    const dispatch = useDispatch();
    const loading = useSelector(state => state.app.administer.loading);
    const youthList = useSelector(state => state.app.careCoordinator.youthList);
    const plansList = useSelector(state => state.app.administer.plansList);
    const youthPlans = useSelector(state => state.app.administer.youthPlans);

    const [startDateValue, setStartDateValue] = useState(null);
    const [selectedPlans, setSelectedPlans] = useState({});
    const [selectedYouth, setSelectedYouth] = useState([]);
    const [youthSelectionTableSelectedRows, setYouthSelectionTableSelectedRows] = useState([]);
    const [filters, setFilters] = useState({
        show: 'all',
        sortBy: 'alerts',
        planName: '',
        search: '',
    });
    const [alertsEditedSignal, setAlertsEditedSignal] = useState(true);

    const assignPlanModalRef = useRef();
    const showFilterRef = useRef();
    const sortByFilterRef = useRef();
    const planNameFilterRef = useRef();

    useEffect(() => {
        dispatch(queryCareCoordinatorYouthList());
        dispatch(queryAdministerPlansList());
    }, [alertsEditedSignal]);

    useEffect(() => {
        dispatch(queryAdministerYouthPlans());
    }, []);

    if (loading) {
        return <Loader loading />;
    }

    function customYouthSorting(a, b) {
        const sortByValue = (filters.sortBy || '').toLowerCase();

        const firstNameA = a.firstName ?? '';
        const firstNameB = b.firstName ?? '';

        const lastNameNameA = a.lastName ?? '';
        const lastNameNameB = b.lastName ?? '';

        const wrapIDA = a.wrapId ?? '';
        const wrapIDB = b.wrapId ?? '';

        switch (sortByValue) {
            case 'alerts':
                return b.flags.length - a.flags.length;
            case 'first_name':
                return firstNameA.localeCompare(firstNameB);
            case 'last_name':
                return lastNameNameA.localeCompare(lastNameNameB);
            case 'wrapid':
                return wrapIDA.localeCompare(wrapIDB);
            default:
                return 0;
        }
    }

    const synchronizeValues = values => {
        if (startDateValue) {
            values.startDate = getFormattedDateForDB(startDateValue);
        }

        values.youthSelected = selectedYouth;
        values.primaryCaregiverPlan = _get(selectedPlans, 'primaryCaregiverPlan[0]', '');
        values.primaryYouthPlan = _get(selectedPlans, 'primaryYouthPlan[0]', '');
        values.orgCaregiverPlan = _get(selectedPlans, 'orgCaregiverPlan[0]', '');
        values.orgYouthPlan = _get(selectedPlans, 'orgYouthPlan[0]', '');

        return values;
    };

    const onHandleSubmit = async values => {
        values = synchronizeValues(values);

        await onSubmit(values);
    };

    const validate = values => {
        const errors = {};

        const validatingValues = synchronizeValues(values);

        if (!validatingValues.primaryCaregiverPlan && !validatingValues.orgCaregiverPlan) {
            errors.selectedPlans = 'A caregiver plan is required to administer.';
        }
        if (validatingValues.primaryYouthPlan && validatingValues.orgYouthPlan) {
            errors.selectedPlans = 'A youth plan is required to administer.';
        }
        if (!validatingValues.startDate) {
            errors.startDate = 'A start date is required to administer.';
        }
        if (!validatingValues.youthSelected || validatingValues.youthSelected.length === 0) {
            errors.youthSelected = 'One or more youth are required to administer.';
        }

        return errors;
    };

    const initialValues = { autorenew: true };

    const onSubmit = async values => {
        const response = await axios.post(Boilerplate.route('app.plan.store'), {
            startDate: values.startDate,
            youthSelected: values.youthSelected,
            orgCaregiverPlan: values.orgCaregiverPlan,
            orgYouthPlan: values.orgYouthPlan,
            primaryCaregiverPlan: values.primaryCaregiverPlan,
            primaryYouthPlan: values.primaryYouthPlan,
            autoRenew: values.autorenew,
        });

        if (response.data.errors) {
            notification({ message: 'Error submitting plan.', type: 'danger' });
            return Promise.reject(response.data.errors);
        }

        notification({ message: 'Plan successfully scheduled.', duration: 5000 });
        await assignPlanModalRef.current.close();

        window.location.reload();
    };

    const getYouthCardsAfterFilters = () => {
        let res = [...youthList].sort(customYouthSorting).map((youth, i) => {
            const activePlans = _get(youthPlans, [youth.pawn.pawnId], []).filter(plan => {
                const status = getPlanStatus(plan);
                return status === 'Active' || status === 'Scheduled';
            });

            const hasActivePlan = activePlans.length > 0;

            const activePlanName = hasActivePlan ? JSON.parse(activePlans[0].settings).planName.toLowerCase() : '';

            const card = (
                <div key={i}>
                    {
                        <YouthCard
                            youth={youth}
                            key={i}
                            i={i}
                            setAlertsEditedSignal={setAlertsEditedSignal}
                            youthPlans={youthPlans}
                            assignPlanModalRef={assignPlanModalRef}
                            setYouthSelectionTableSelectedRows={setYouthSelectionTableSelectedRows}
                        />
                    }
                </div>
            );

            const blank = null;

            if (filters) {
                const showFilterValue = filters.show;
                const searchValue = (filters.search || '').toLowerCase();
                const planNameValue = (filters.planName || '').toLowerCase();

                const youthName = `${youth.fields.firstName || ''} ${youth.fields.lastName || ''}`.toLowerCase();
                const youthWrapId = (youth.fields.wrapId || '').toLowerCase();

                const caregiverNames = youth.parentRoles.caregiverList
                    .map(caregiver =>
                        `${caregiver.fields.firstName || ''} ${caregiver.fields.lastName || ''}`.toLowerCase()
                    )
                    .join(' ');

                const caregiverWrapIds = youth.parentRoles.caregiverList
                    .map(caregiver => {
                        if (caregiver.fields.wrapId) {
                            return caregiver.fields.wrapId.toLowerCase();
                        }
                        return '';
                    })
                    .join(' ');

                const searchableFields = [youthName, youthWrapId, caregiverNames, caregiverWrapIds];

                const matchesPlanName = !planNameValue || activePlanName === planNameValue;

                const matchesSearch = !searchValue || searchableFields.some(field => field?.includes(searchValue));

                if (!matchesPlanName || !matchesSearch) return blank;

                switch (showFilterValue) {
                    case 'active_plan':
                        return hasActivePlan ? card : blank;
                    case 'no_plan':
                        return !hasActivePlan ? card : blank;
                    default:
                        return card;
                }
            }

            return blank;
        });

        res = res.filter(val => val !== null);

        if (res.length === 0) {
            return noYouthWarning({ message: 'No Youth found with selected filters.' });
        }

        return res;
    };

    return (
        <div>
            <div style={{ padding: '30px' }}>
                {youthList.length === 0 ? (
                    noYouthWarning({
                        message: (
                            <div>
                                Welcome to SMART-Wrap! To get started, please navigate to the
                                <a href="/manage" style={{ color: '#4a2e84' }}>
                                    {' '}
                                    <u>Manage</u>{' '}
                                </a>
                                tab above to add your families.
                            </div>
                        ),
                        showIcon: false,
                    })
                ) : (
                    <div>
                        <div className={style.new_plan_button_box}>
                            <div style={{ marginRight: '15px' }}>
                                <Modal
                                    trigger={
                                        <button type="button" className="btn btn-dark">
                                            Assign New Plan
                                        </button>
                                    }
                                    ref={assignPlanModalRef}
                                    title="Assign New Plan"
                                    size="80vw"
                                    render={({ closeModal }) => {
                                        return (
                                            <div style={{ margin: '15px' }}>
                                                <Formik
                                                    onSubmit={e => onHandleSubmit(e)}
                                                    validate={e => validate(e)}
                                                    initialValues={initialValues}
                                                >
                                                    {({ handleSubmit, values, setFieldValue }) => (
                                                        <Form>
                                                            <div className={style.assign_plan_section}>
                                                                <div className={style.number}>1</div>
                                                                <div>
                                                                    <Field name="startDate">
                                                                        {({ field, form }) => {
                                                                            return (
                                                                                <CccisdFieldWrapper
                                                                                    label={
                                                                                        <div>
                                                                                            <RequiredFieldLabel
                                                                                                isRequired
                                                                                            >
                                                                                                Start Date:
                                                                                            </RequiredFieldLabel>
                                                                                        </div>
                                                                                    }
                                                                                    form={form}
                                                                                    field={field}
                                                                                >
                                                                                    <div>
                                                                                        <DatePicker
                                                                                            id="startDateValue"
                                                                                            selected={
                                                                                                startDateValue
                                                                                                    ? startDateValue
                                                                                                    : null
                                                                                            }
                                                                                            filterDate={isMonday}
                                                                                            onChange={val => {
                                                                                                setStartDateValue(val);
                                                                                            }}
                                                                                            placeholderText="-- Select a Monday to start --"
                                                                                            dateFormat="MM/dd/yyyy"
                                                                                        />
                                                                                    </div>
                                                                                    <div
                                                                                        style={{
                                                                                            marginTop: '20px',
                                                                                        }}
                                                                                        className={style.tooltip_text}
                                                                                    >
                                                                                        Start Date determines the first
                                                                                        date that messages will start.
                                                                                        Messages are sent on Mondays
                                                                                        ~10AM EST for caregivers and
                                                                                        ~4PM EST for youth.
                                                                                    </div>
                                                                                </CccisdFieldWrapper>
                                                                            );
                                                                        }}
                                                                    </Field>
                                                                </div>
                                                            </div>
                                                            <div
                                                                className={style.assign_plan_section}
                                                                style={{ flexWrap: 'wrap' }}
                                                            >
                                                                <div className={style.number}>2</div>

                                                                <div className={style.select_youth_table_wrapper}>
                                                                    <Field name="youthSelected">
                                                                        {({ field, form }) => {
                                                                            return (
                                                                                <CccisdFieldWrapper
                                                                                    label={
                                                                                        <div
                                                                                            style={{
                                                                                                marginBottom: '8px',
                                                                                            }}
                                                                                        >
                                                                                            <RequiredFieldLabel
                                                                                                isRequired
                                                                                            >
                                                                                                Select Youth:
                                                                                            </RequiredFieldLabel>
                                                                                        </div>
                                                                                    }
                                                                                    form={form}
                                                                                    field={field}
                                                                                >
                                                                                    <>
                                                                                        <YouthSelectionTable
                                                                                            youthList={youthList}
                                                                                            youthPlans={youthPlans}
                                                                                            setSelectedYouth={
                                                                                                setSelectedYouth
                                                                                            }
                                                                                            youthSelectionTableSelectedRows={
                                                                                                youthSelectionTableSelectedRows
                                                                                            }
                                                                                        />
                                                                                    </>
                                                                                </CccisdFieldWrapper>
                                                                            );
                                                                        }}
                                                                    </Field>
                                                                </div>
                                                            </div>
                                                            <div className={style.assign_plan_section}>
                                                                <div
                                                                    className={style.number}
                                                                    style={{ marginBottom: '25px' }}
                                                                >
                                                                    3
                                                                </div>

                                                                <div className={style.flex_column}>
                                                                    <Field
                                                                        name="autorenew"
                                                                        component={CccisdToggle}
                                                                        label={<b>Auto Renew Plan</b>}
                                                                    />
                                                                    {values.autorenew && (
                                                                        <div className={style.tooltip_text}>
                                                                            The SMS survey messages will re-start once
                                                                            the last survey message of the plan is sent.
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            <div
                                                                className={style.assign_plan_section}
                                                                style={{
                                                                    borderBottom: '0px',
                                                                }}
                                                            >
                                                                <div className={style.number}>4</div>

                                                                <div style={{ width: '90%' }}>
                                                                    <Field name="selectedPlans">
                                                                        {({ field, form }) => {
                                                                            return (
                                                                                <CccisdFieldWrapper
                                                                                    label={
                                                                                        <div>
                                                                                            <RequiredFieldLabel
                                                                                                isRequired
                                                                                            >
                                                                                                Select Plan(s) to
                                                                                                administer:
                                                                                            </RequiredFieldLabel>
                                                                                        </div>
                                                                                    }
                                                                                    form={form}
                                                                                    field={field}
                                                                                />
                                                                            );
                                                                        }}
                                                                    </Field>
                                                                    {Object.keys(plansList).map((planPhase, i) => (
                                                                        <div key={i}>
                                                                            <PlanCard
                                                                                plan={plansList[planPhase]}
                                                                                selectedPlans={selectedPlans}
                                                                                setSelectedPlans={setSelectedPlans}
                                                                            />
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>

                                                            <div className={style.submit_button}>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-secondary"
                                                                    onClick={() => handleSubmit()}
                                                                >
                                                                    Submit
                                                                </button>
                                                            </div>
                                                        </Form>
                                                    )}
                                                </Formik>
                                            </div>
                                        );
                                    }}
                                />
                            </div>
                            <div>Administer plan(s) consisting of weekly surveys to youth and their caregivers</div>
                        </div>
                        <br />

                        {/* filter area  & youth cards container */}
                        <div className={style.main_container}>
                            {/* filters container  */}
                            <div className={style.filters_container}>
                                <div className={style.filters_flex_row}>
                                    <div>
                                        <label className="form-label">Search: </label>
                                        <input
                                            className="form-control"
                                            type="text"
                                            placeholder="Name / WrapID"
                                            value={filters.search}
                                            onChange={e => {
                                                const newValue = e.target.value;
                                                setFilters(prev => ({
                                                    ...prev,
                                                    search: newValue,
                                                }));
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <label className="form-label">Show: </label>
                                        <select
                                            className="form-select"
                                            ref={showFilterRef}
                                            onChange={e => {
                                                const newValue = e.target.value;
                                                setFilters(prev => ({
                                                    ...prev,
                                                    show: newValue,
                                                }));
                                            }}
                                        >
                                            <option value="all">All Youth</option>
                                            <option value="active_plan">With Active Plan</option>
                                            <option value="no_plan">Without Active Plan</option>
                                            {/* <option value={'alerts'}>With Alerts</option> */}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="form-label">Sort By: </label>
                                        <select
                                            className="form-select"
                                            ref={sortByFilterRef}
                                            onChange={e => {
                                                const newValue = e.target.value;
                                                setFilters(prev => ({
                                                    ...prev,
                                                    sortBy: newValue,
                                                }));
                                            }}
                                        >
                                            <option value="alerts">Alerts</option>
                                            <option value="first_name">First Name</option>
                                            <option value="last_name">Last Name</option>
                                            <option value="wrapid">WrapID</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="form-label">Plan Name: </label>
                                        <select
                                            className="form-select"
                                            ref={planNameFilterRef}
                                            value={filters.planName}
                                            onChange={e => {
                                                const newValue = e.target.value;
                                                setFilters(prev => ({
                                                    ...prev,
                                                    planName: newValue,
                                                }));
                                            }}
                                        >
                                            <option value="">All</option>
                                            {Object.keys(plansList)
                                                .filter(val => val)
                                                .map((planName, i) => {
                                                    return (
                                                        <option key={i} value={planName.toLowerCase()}>
                                                            {switchPlanNameIfNeeded(planName)}
                                                        </option>
                                                    );
                                                })}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* youth cards start */}
                            {getYouthCardsAfterFilters()}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Administer;
